import { useCallback, useEffect, useState } from "react";
import io from "socket.io-client";

import config from "../config/config";
import { getToken, getUserData } from "../common";
import { getPendingJoinRequestCountsAPI } from "../api/joinRequestsAPI";

const EMPTY = { total: 0, byEvent: {}, auctions: [], latest: [] };
const CHANGED_EVENT = "v3bid:join-requests-changed";

// tells every count on the page (menu, list, tab) to reload after a decision
export const notifyJoinRequestsChanged = () =>
  window.dispatchEvent(new Event(CHANGED_EVENT));

/**
 * Requests to participate still waiting for a decision, for an organiser's
 * own auctions (every auction for an admin): { total, byEvent, latest }.
 * Bidders get nothing. Updated live when a bidder sends or withdraws one.
 */
const usePendingJoinRequests = () => {
  const userData = getUserData() ?? {};
  const decides = userData.role === "SELLER" || userData.role === "ADMIN";
  const [pending, setPending] = useState(EMPTY);

  const reload = useCallback(async () => {
    if (!decides) return;
    try {
      const { data } = await getPendingJoinRequestCountsAPI();
      setPending({ ...EMPTY, ...data });
    } catch (error) {
      // counts are a hint only; keep the last ones
    }
  }, [decides]);

  useEffect(() => {
    if (!decides) return undefined;
    reload();
    const socket = io(config.api.socket_url, {
      extraHeaders: { authorization: getToken() },
    });
    socket.on(`${userData.userId}`, (props) => {
      if (props?.joinRequest) reload();
    });
    window.addEventListener(CHANGED_EVENT, reload);
    return () => {
      socket.disconnect();
      window.removeEventListener(CHANGED_EVENT, reload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decides, userData.userId]);

  return { ...pending, reload };
};

export default usePendingJoinRequests;
